import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Loader, Text, Select, Group, Divider, Card } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type { ExternalObjectConf } from '@bageltechnologies/bagel-common';
import { CustomQueryBuilder } from '../CustomQueryBuilder';
import type { Field, RuleGroupType } from 'react-querybuilder';

interface CountExplorationModalProps {
  opened: boolean;
  onClose: () => void;
  externalObject: ExternalObjectConf | null;
  isLoadingObject: boolean;
  onCount: (dateRange?: { start: string; end: string }, filters?: any, dateField?: string) => void;
  isCounting: boolean;
  integrationKey: string;
}

export const CountExplorationModal: React.FC<CountExplorationModalProps> = ({
  opened,
  onClose,
  externalObject,
  isLoadingObject,
  onCount,
  isCounting,
  integrationKey,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateField, setDateField] = useState<string | undefined>(undefined);
  const [localFilterValue, setLocalFilterValue] = useState<RuleGroupType | null>(null);
  const [countResult, setCountResult] = useState<number | null>(null);
  const [dateFieldErrorVisible, setDateFieldErrorVisible] = useState<boolean>(false);
  const [datePickerPortalTarget, setDatePickerPortalTarget] = useState<HTMLDivElement | null>(null);

  // Portal container for date picker dropdown so it renders above the modal
  useEffect(() => {
    if (!opened) return;
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      zIndex: '99999',
      pointerEvents: 'none',
    });
    document.body.appendChild(el);
    setDatePickerPortalTarget(el);
    return () => {
      if (el.parentNode) document.body.removeChild(el);
      setDatePickerPortalTarget(null);
    };
  }, [opened]);

  // Reset date range when modal opens
  useEffect(() => {
    if (opened) {
      // Set default date range
      const now = new Date();
      const daysAgo = new Date();
      daysAgo.setDate(now.getDate() - 30);

      setStartDate(daysAgo);
      setEndDate(now);
      setDateField(undefined);
    }
  }, [opened]);

  // Prepare query builder fields like in FilterPopup
  const queryBuilderFields: Field[] = useMemo(() => {
    const extendedObj = externalObject as ExternalObjectConf | null;
    if (!extendedObj || !extendedObj.fieldsMeta) return [];
    return extendedObj.fieldsMeta.map((field: any) => {
      let valueEditorType = 'text';
      if (field.type === 'boolean') valueEditorType = 'checkbox';
      else if (field.type === 'number') valueEditorType = 'number';
      else if (field.type === 'date') valueEditorType = 'date';

      let operators = undefined as any;
      if (field.type === 'boolean') {
        operators = [
          { name: '=', label: 'is' },
          { name: '!=', label: 'is not' },
        ];
      }

      return {
        name: field.fieldPath || field.name || field.key,
        label: field.label || field.name || field.key,
        valueEditorType,
        operators,
      } as Field;
    });
  }, [externalObject]);

  // Build date field options from externalObject.fieldsMeta (type date or datetime)
  const dateFieldOptions = useMemo(() => {
    const ext = externalObject as ExternalObjectConf | null;
    if (!ext || !Array.isArray((ext as any)?.fieldsMeta))
      return [] as { value: string; label: string }[];
    return (ext as any).fieldsMeta
      .filter((f: any) => f?.type === 'date' || f?.type === 'datetime')
      .map((f: any) => ({
        value: f.fieldPath || f.name || f.key,
        label: f.label || f.name || f.key,
      }));
  }, [externalObject]);

  // Initialize local filters when modal opens/externalObject changes
  useEffect(() => {
    if (opened) {
      const ext = externalObject as ExternalObjectConf | null;
      setLocalFilterValue((ext?.filters as RuleGroupType) || { combinator: 'and', rules: [] });
      setCountResult(null); // Reset count result when modal opens
    }
  }, [opened, externalObject]);

  const handleCount = async () => {
    // validation: date field required on submit
    setDateFieldErrorVisible(false);
    if (!dateField) {
      setDateFieldErrorVisible(true);
      return;
    }
    // reset previous result so footer shows placeholder while counting
    setCountResult(null);
    const dateRange =
      startDate && endDate
        ? {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
          }
        : undefined;

    const filters = (localFilterValue as any) || { combinator: 'and', rules: [] };

    // Pass dateRange, filters and dateField as separate parameters
    try {
      const result = await onCount(dateRange, filters, dateField);
      // If onCount returns a count, store it
      if (typeof result === 'number') {
        setCountResult(result);
      }
    } catch (error) {
      console.error('Count failed:', error);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Get Object Count`}
      centered
      size="lg"
      zIndex={500}
      styles={{ content: { overflow: 'visible' }, body: { overflow: 'visible' } }}
    >
      {isLoadingObject ? (
        <Loader />
      ) : (
        <>
          {/* Date Field row */}
          <Group mb="xs" align="center" spacing="md" noWrap>
            <Text size="sm" weight={500} sx={{ minWidth: 100 }}>
              Date field
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Select
                placeholder="Choose"
                data={dateFieldOptions}
                value={dateField || null}
                onChange={(value) => setDateField(value || undefined)}
                disabled={isCounting}
                sx={{ minWidth: 240 }}
              />
              {dateFieldErrorVisible && (
                <Text size="xs" color="red" sx={{ marginTop: 6 }}>
                  Date field is required
                </Text>
              )}
            </div>
          </Group>

          {/* Get count range row */}
          <Group mb="md" align="center" spacing="md" noWrap>
            <Text size="sm" weight={500} sx={{ minWidth: 100 }}>
              Get count from
            </Text>
            <DatePickerInput
              placeholder="Start date"
              value={startDate}
              onChange={setStartDate}
              disabled={isCounting}
              style={{ width: 240 }}
              popoverProps={{
                withinPortal: true,
                zIndex: 100000,
                position: 'bottom-start',
                middlewares: { flip: false, shift: false },
                ...(datePickerPortalTarget && { portalProps: { target: datePickerPortalTarget } }),
                styles: { dropdown: { pointerEvents: 'auto' } },
              }}
            />
            <Text size="sm" weight={500}>
              through
            </Text>
            <DatePickerInput
              placeholder="End date"
              value={endDate}
              onChange={setEndDate}
              disabled={isCounting}
              style={{ width: 240 }}
              popoverProps={{
                withinPortal: true,
                zIndex: 100000,
                position: 'bottom-start',
                middlewares: { flip: false, shift: false },
                ...(datePickerPortalTarget && { portalProps: { target: datePickerPortalTarget } }),
                styles: { dropdown: { pointerEvents: 'auto' } },
              }}
            />
          </Group>

          {/* <Select
            label="Date Field"
            data={[
              { value: 'updatedAt', label: 'Updated At' },
              { value: 'createdAt', label: 'Created At' },
            ]}
            value={dateField}
            onChange={(value) => setDateField(value || 'updatedAt')}
            disabled={isCounting}
            mb="md"
          /> */}

          <Text size="sm" weight={500} mb="xs">
            Filters (Optional):
          </Text>
          {queryBuilderFields.length > 0 ? (
            <Card withBorder p="sm" mb="md">
              <CustomQueryBuilder
                fields={queryBuilderFields}
                // Fallback to empty rule group
                // @ts-ignore
                query={(localFilterValue as RuleGroupType) || { combinator: 'and', rules: [] }}
                onQueryChange={(q: RuleGroupType) => setLocalFilterValue(q)}
                addRuleToNewGroups={false}
                autoSelectField
                autoSelectOperator
                showCombinatorsBetweenRules={false}
                showNotToggle={false}
                showCloneButtons={false}
                resetOnFieldChange
                resetOnOperatorChange={false}
              />
            </Card>
          ) : (
            <Text color="dimmed" size="sm" mb="md">
              No fields available for filtering for the selected object.
            </Text>
          )}

          <Group position="apart" mt="md">
            <Button
              onClick={handleCount}
              loading={isCounting}
              disabled={isCounting || !startDate || !endDate}
              sx={{
                '&:disabled': {
                  backgroundColor: '#4c6ef5', // Green with 30% opacity
                  color: '#fff',
                  borderColor: '#4c6ef5',
                  opacity: 0.3,
                },
              }}
            >
              Show count
            </Button>
            {isCounting || countResult !== null ? (
              <Button
                variant="outline"
                disabled
                color="blue"
                size="md"
                sx={{ color: '#000', '&:disabled': { color: '#000' } }}
              >
                {isCounting ? '----' : `${(countResult ?? 0).toLocaleString()} objects`}
              </Button>
            ) : (
              <Button variant="default" onClick={onClose} disabled={isCounting}>
                Cancel
              </Button>
            )}
          </Group>
        </>
      )}
    </Modal>
  );
};
